 
 
import { Component, OnChanges, SimpleChanges, ViewChild, Input, input, Output, EventEmitter, signal, computed, forwardRef, booleanAttribute, ElementRef } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { Observable } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-lt-multiselect-t',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatChipsModule,
    MatInputModule,
    MatIconModule,
    MatAutocompleteModule,
  ],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LTMultiSelectTComponent),
      multi: true,
    },
  ],
  template: `
    @if(data !== null && data?.length > 0) {
      <mat-form-field class="full-width" [appearance]="fillMode">
        @if(label())
        {
          <mat-label>{{label()}}</mat-label>
        }
        <mat-chip-grid #chipGrid>
          <!-- mat-chip-grid only counts mat-chip-row children (its MatFormFieldControl 'empty' state). Plain mat-chip
               elements render but are invisible to the grid, so the form field always treated the box as empty and the
               label stayed over the chips whenever the field wasn't focused. -->
          @for(item of selectedItems; track $index) {
              <mat-chip-row [disabled]="isDisabled"
                  [removable]="true"
                  (removed)="remove(item)">
                  {{ getDisplayValue(item) }}
                  <button matChipRemove [attr.disabled]="isDisabled ? true : null">
                    <mat-icon>cancel</mat-icon>
                  </button>
              </mat-chip-row>
          }
          
          <input #itemInput
            [placeholder]="placeholder()"
            [formControl]="itemCtrl"
            [matAutocomplete]="auto"
            [matChipInputFor]="chipGrid"
            matChipInputSeparatorKeyCodes="separatorKeysCodes"
            (blur)="onTouched()">
        </mat-chip-grid>

        <mat-autocomplete 
          autoActiveFirstOption
          [panelWidth]="panelWidth()"
          #auto="matAutocomplete" 
          (optionSelected)="selected($event)"
          [displayWith]="displayFn">
          @for(item of filteredItems | async; track $index) {
              <mat-option [value]="item">
                {{ getDisplayValue(item) }}
              </mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
    } @else {
      {{ noItemsMessage() }}
    }
  `,
  styles: `
  mat-form-field {
    width: 100%;
    min-width: 0;
  }
  .chip-list-field {
    width: 100%;
  }
  .disabled {
    opacity: 0.7;
    pointer-events: none;
  }
`,
})
export class LTMultiSelectTComponent<T> implements ControlValueAccessor, OnChanges {
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  //#region Inputs
  @Input() fillMode: 'outline' | 'fill' = 'outline';
  @Input() data: T[] = [];
  label = input('');
  placeholder = input('Select options');
  noItemsMessage = input<string>('No items available.');
  textField = input<keyof T | null>(null);
  valueField = input<keyof T | null>(null);
  required = input(false, {transform: booleanAttribute});
  panelWidth = input<string | number | null>('auto'); // auto = match trigger width, null / empty string = match longest option width, # = width in px

  //#region Disabled
  @Input({transform: booleanAttribute}) set disabled(isDisabled: boolean) {
    if (isDisabled) {
      this.itemCtrl.disable();
    }
    else {
      this.itemCtrl.enable();
    }
  }
  get disabled(): boolean {
    return this.itemCtrl.disabled;
  }
  //#endregion

  //#region value
  private _value = signal<T[]>([]);
  @Input() set value(val: T[]) {
    if (val !== undefined && val !== null)
    {
      this._setValue(val);
    }
  }
  get value(): T[] {
    return this._value();
  }
  //#endregion

  //#endregion Inputs

  // computed properties
  displayField = computed(() => this.textField());
  idField = computed(() => this.valueField());

  displayFn = (item: T): string => {
    return item ? this.getDisplayValue(item): '';
  };
  
  // FormControl
  @ViewChild('itemInput') itemInput!: ElementRef<HTMLInputElement>;
  itemCtrl = new FormControl<string | T | null>(null);
  filteredItems: Observable<T[]>;
  selectedItems: T[] = [];

  // Output
  @Output() valueChange = new EventEmitter<T[]>();
  @Output() selectionChange = new EventEmitter<T[]>();

  // ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor() {
    this.filteredItems = this.itemCtrl.valueChanges.pipe(
      startWith(null),
      map(value => {
        if (typeof value === 'string') {
          return this._filter(value);
        }
        return this._filter('');
      }),
    );

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.filteredItems = this.itemCtrl.valueChanges.pipe(
        startWith(null),
        map(value => {
          if (typeof value === 'string') {
            return this._filter(value);
          }
          return this._filter('');
        }),
      );
    }
  }

  getDisplayValue(item: T): string {
    return item[this.displayField()] as unknown as string;
  }

  getIdValue(item: T): any {
    return item[this.idField()];
  }

  remove(item: T): void {
    const index = this.selectedItems.findIndex(
      selected => this.getIdValue(selected) === this.getIdValue(item),
    );

    if (index >= 0) {
      const updatedItems = [...this.selectedItems];
      updatedItems.splice(index, 1);
      this.selectedItems = updatedItems;

      this._emitChangeEvent();
    
      // Trigger the filteredItems observable to refresh
      this.itemCtrl.setValue(this.itemCtrl.value);
    }
  }

  selected(event: any): void {
    const selectedItem = event.option.value as T;

    if (!this.selectedItems.some(item => this.getIdValue(item) === this.getIdValue(selectedItem))) {
      this.selectedItems = [...this.selectedItems, selectedItem];
      this._emitChangeEvent();
    }

    this.itemInput.nativeElement.value = '';
    this.itemCtrl.setValue(null);
  }

  private _filter(value: string): T[] {
    const filterValue = value.toLowerCase();
    const sourceData = this.data && this.data.length > 0 ? this.data : [];
    
    return sourceData.filter(item => 
      this.getDisplayValue(item).toLowerCase().includes(filterValue) &&
      !this.selectedItems.some(o => this.getIdValue(o) === this.getIdValue(item)),
    );
  }

  private _emitChangeEvent(): void {
    this.onChange(this.selectedItems);
    this.onTouched();

    this.valueChange.emit(this.selectedItems);
    this.selectionChange.emit(this.selectedItems);
  }

  private _setValue(val: T[]): void {
    if (val !== this.selectedItems) {
      this.selectedItems = val || [];
      this.onChange(this.selectedItems);
    
      // Trigger the filteredItems observable to refresh when value is set programmatically
      this.itemCtrl.setValue(this.itemCtrl.value);
    }
  }

  writeValue(items: T[]): void {
    this.selectedItems = Array.isArray(items) ? [...items] : [];
    
    // Trigger the filteredItems observable to refresh when value is written by forms
    this.itemCtrl.setValue(this.itemCtrl.value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled){
      this.itemCtrl.disable();
    }
    else {
      this.itemCtrl.enable();
    }
  }
}
